import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toPng } from "html-to-image";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_import-hub-80/artifacts/r714uk97_BotBSlogo%20RGB.png";
const HERO_FALLBACK = "https://customer-assets.emergentagent.com/job_import-hub-80/artifacts/owuwhzm5_IMG_1370.jpeg";

const DIMS = { square: { w: 1080, h: 1080 }, story: { w: 1080, h: 1920 } };

function Ridge({ fill = "#0E2C46", height = 44, flip = false }) {
  return (
    <svg viewBox="0 0 1400 110" preserveAspectRatio="none" aria-hidden
      style={{ display: "block", width: "100%", height, transform: flip ? "scaleY(-1)" : "none" }}>
      <path d="M0,110 L0,80 L90,55 L180,75 L270,30 L370,70 L460,40 L560,80 L660,25 L760,65 L860,35 L960,75 L1060,30 L1170,70 L1270,40 L1360,75 L1400,55 L1400,110 Z" fill={fill} />
      <path d="M0,110 L0,95 L110,80 L230,90 L330,72 L450,90 L570,78 L690,92 L810,75 L930,90 L1050,80 L1190,92 L1290,82 L1400,90 L1400,110 Z" fill={fill} opacity="0.55" />
    </svg>
  );
}

export default function FlyerSocialPage() {
  const { variant = "square" } = useParams();
  const v = DIMS[variant] ? variant : "square";
  const { w, h } = DIMS[v];
  const [sale, setSale] = useState(null);
  const [settings, setSettings] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    document.title = `Legacy Sale ${v === "story" ? "Story" : "Square"} Flyer — Bulls of the Big Sky`;
    (async () => {
      try {
        const [s, set] = await Promise.all([axios.get(`${API}/sales/legacy`), axios.get(`${API}/site-settings`)]);
        setSale(s.data); setSettings(set.data);
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.error("Failed to load flyer data:", e);
      }
    })();
  }, [v]);

  const download = useCallback(async () => {
    if (!canvasRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(canvasRef.current, { width: w, height: h, pixelRatio: 1, cacheBust: true });
      const a = document.createElement("a");
      a.download = `legacy-sale-${v}.png`;
      a.href = dataUrl;
      a.click();
    } finally {
      setDownloading(false);
    }
  }, [v, w, h]);

  const phone = settings?.contact_phone || "208-267-2668";
  const hero = sale?.hero_image_url || HERO_FALLBACK;
  const story = v === "story";
  const scale = Math.min(1, 560 / w);

  return (
    <div data-testid={`flyer-social-${v}`} style={{ background: "#2d3a4a", minHeight: "100vh", padding: "24px 0 64px" }}>
      {/* Toolbar */}
      <div className="no-print" style={{ maxWidth: 560 * (story ? 1 : 1), margin: "0 auto 20px", background: "var(--bg-primary)", border: "1px solid var(--border-subtle)", padding: "16px 20px", width: 560 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="eyebrow" style={{ color: "var(--brass)" }}>{story ? "Instagram Story  •  1080 × 1920" : "Instagram Post  •  1080 × 1080"}</div>
            <div className="font-display text-2xl mt-1" style={{ color: "var(--brass-deep)" }}>Legacy Sale Flyer</div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button type="button" data-testid="flyer-download-btn" onClick={download} disabled={downloading} className="btn-primary">
              {downloading ? "Rendering…" : "Download PNG"}
            </button>
            <Link to={story ? "/flyer/legacy/square" : "/flyer/legacy/story"} data-testid="flyer-switch-variant" className="btn-ghost">
              {story ? "Square Version" : "Story Version"}
            </Link>
            <Link to="/flyer/legacy" data-testid="flyer-back-print" className="btn-ghost">Print Flyer</Link>
          </div>
        </div>
      </div>

      {/* Scaled preview wrapper */}
      <div style={{ width: w * scale, height: h * scale, margin: "0 auto", boxShadow: "0 24px 60px rgba(0,0,0,0.45)" }}>
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
          {/* ===== The 1080px canvas ===== */}
          <div ref={canvasRef} style={{
            width: w, height: h, background: "#F4EFE3", color: "#13202F",
            display: "flex", flexDirection: "column", overflow: "hidden", position: "relative",
            fontFamily: "'Manrope', sans-serif",
          }}>
            {/* Brand band */}
            <div style={{ background: "#0E2C46", color: "#fff", padding: story ? "44px 56px 30px" : "34px 48px 24px", display: "flex", alignItems: "center", gap: 26 }}>
              <img src={LOGO_URL} alt="" crossOrigin="anonymous" style={{ width: story ? 130 : 110, height: story ? 130 : 110, objectFit: "contain" }} />
              <div>
                <div style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 800, fontSize: story ? 52 : 44, lineHeight: 1, textTransform: "uppercase" }}>Bulls of the Big Sky</div>
                <div style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600, fontSize: story ? 17 : 15, letterSpacing: "0.34em", color: "#BFD4E0", marginTop: 10 }}>SIMMENTAL ★ ANGUS ★ SIMANGUS</div>
              </div>
            </div>
            <Ridge height={story ? 52 : 40} />

            {/* Hero */}
            <div style={{
              flex: 1, position: "relative", color: "#fff",
              backgroundImage: `linear-gradient(180deg, rgba(14,44,70,0.28) 0%, rgba(14,44,70,0.5) 55%, rgba(14,44,70,0.92) 100%), url('${hero}')`,
              backgroundSize: "cover", backgroundPosition: "center 55%",
              display: "flex", flexDirection: "column", justifyContent: "flex-end",
              padding: story ? "0 64px 72px" : "0 56px 48px",
            }}>
              <div style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 700, fontSize: story ? 22 : 18, letterSpacing: "0.36em", color: "#E8D7A8", marginBottom: 10 }}>
                THE INAUGURAL LEGACY SALE
              </div>
              <div style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 900, fontSize: story ? 124 : 96, lineHeight: 0.9, textTransform: "uppercase", textShadow: "0 4px 18px rgba(14,44,70,0.55)" }}>
                Legacy of<br />the Big Sky
              </div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontWeight: 500, fontSize: story ? 68 : 54, color: "#E8D7A8", lineHeight: 1, margin: "18px 0 10px" }}>
                December 5, 2026
              </div>
              <div style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600, fontSize: story ? 24 : 19, letterSpacing: "0.24em", textTransform: "uppercase", color: "rgba(255,255,255,0.92)" }}>
                Billings Livestock Commission  •  1:00 PM MT
              </div>
            </div>

            {/* Stats band */}
            <div style={{ background: "#F4EFE3", padding: story ? "48px 64px 40px" : "36px 56px 28px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, borderBottom: "1px solid rgba(30,77,117,0.18)" }}>
              {[["80", "Age-Advantaged Bulls"], ["100", "Commercial Bred Heifers"], ["8", "Montana Programs"]].map(([n, l], i) => (
                <div key={l}>
                  <div style={{
                    fontFamily: "'Big Shoulders Display', sans-serif",
                    fontWeight: 900, fontSize: story ? 110 : 88, lineHeight: 0.85,
                    color: "#1E4D75",
                  }}>{n}</div>
                  <div style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 800, fontSize: story ? 26 : 21, textTransform: "uppercase", letterSpacing: "0.06em", color: "#0E2C46", marginTop: 8, lineHeight: 1.05 }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Footer strip */}
            <div style={{ background: "#0E2C46", color: "#fff", padding: story ? "36px 64px 40px" : "26px 56px 30px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20 }}>
              <div>
                <div style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 700, fontSize: story ? 17 : 14, letterSpacing: "0.36em", color: "#BFD4E0" }}>2026 SALE BOOK</div>
                <div style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 900, fontSize: story ? 54 : 42, lineHeight: 0.95, textTransform: "uppercase", marginTop: 6 }}>Request a Catalog</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 800, fontSize: story ? 30 : 24, letterSpacing: "0.04em" }}>bullsofthebigsky.com</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: story ? 30 : 24, color: "#E8D7A8", marginTop: 4 }}>{phone}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
